<template>
    <div class="custom-scroll px-2">
        <ConfirmationAlert
            :code="confirm.code"
            @confirm-action="confirmAction"
            @cancel-action="cancelAction"
            :inital_text="confirm.text"
            :inital_title="confirm.title"
            v-if="confirm.showConfirmation"
        ></ConfirmationAlert>

        <div class="modal-header bg-body mb-4">
            <h3 class="fs-5 fw-medium ppv2-text-black block-title m-0">{{ $t('planningModal.title') }}</h3>
            <a class="close btn-close" data-dismiss="modal" aria-label="Close" id="close_btn" @click="closeModal"> </a>
        </div>

        <div class="modal-body pb-0">
            <h6 class="bg-danger text-white p-4 row mb-3" v-if="!canEditTime">
                {{ $t('planningModal.cannotEditTime') }}
            </h6>
            <div class="row mb-3">
                <label class="form-label fw-semibold" for="date-range"
                    >{{ $t('planningModal.selectDateRange') }} <span class="text-danger"> *</span></label
                >
                <date-range-picker
                    v-model="dateRange"
                    :ranges="ranges"
                    :locale-data="{ firstDay: 1, format: 'dd-mm-yyyy' }"
                    id="date-range"
                    :disabled="!canChange"
                ></date-range-picker>
            </div>

            <div class="days" v-if="canChange">
                <label class="form-label fw-semibold">{{ $t('planningModal.dayRepetition') }}</label>

                <div class="d-flex flex-wrap gap-4 mt-1">
                    <span v-for="(day, index) in days" :key="index">
                        <label class="form-label fw-semibold">
                            <input type="checkbox" :value="day" v-model="selectedDays" />&nbsp;{{ translateDay(day) }}
                        </label>
                    </span>
                </div>
            </div>

            <div class="d-flex flex-row-reverse">
                <button
                    type="button"
                    class="btn btn-add-date fs-5 text-white mt-4 mb-2"
                    :disabled="items.length === maxItemsNbr"
                    @click="addItem"
                >
                    +
                </button>
            </div>

            <div class="row mb-3 border-bottom" v-for="(item, index) in items" :key="index">
                <div class="d-flex mt-3 mb-4">
                    <h6 class="fw-medium fs-6 mt-3 mb-2 ppv2-text-black">{{ $t('planningModal.creneau') }} : {{ index + 1 }}</h6>

                    <button
                        type="button"
                        class="btn btn-delete-date mt-2 ms-3"
                        @click="deleteItem(index)"
                        :title="$t('planningModal.deleteCreneau')"
                    >
                        <i class="fa fa-fw fa-minus fs-xs text-grey"></i>
                    </button>
                </div>
                <div class="col-12">
                    <div class="d-flex mt-2">
                        <h6 class="fs-6 fw-medium ppv2-text-black pt-1">{{ $t('planningModal.peakHour') }}</h6>
                        <div class="form-check form-switch me-4">
                            <input type="checkbox" v-model="item.off_peak_time" class="form-control form-check-input" />
                        </div>
                        <h6 class="fs-6 fw-medium ppv2-text-black pt-1">{{ $t('planningModal.offPeakHour') }}</h6>
                    </div>
                </div>
                <div class="col-12">
                    <div class="d-flex mt-1 mb-2">
                        <h6 class="fs-6 fw-medium ppv2-text-black pt-1">{{ $t('planningModal.forbidMixing') }}</h6>
                        <div class="form-check form-switch me-4">
                            <input type="checkbox" v-model="item.authorize_mixing" class="form-control form-check-input" />
                        </div>
                        <h6 class="fs-6 fw-medium ppv2-text-black pt-1">{{ $t('planningModal.allowMixing') }}</h6>
                    </div>
                </div>

                <div class="col-sm-6 col-xs-12">
                    <label class="form-label fw-semibold" :for="'start-time-' + index"
                        >{{ $t('planningModal.startTime') }} <span class="text-purple">*</span></label
                    >
                    <div class="d-flex">
                        <input
                            type="time"
                            :id="'start-time-' + index"
                            class="form-control"
                            style="background: transparent; border-radius: 14px !important; z-index: 50"
                            v-model="item.startTime"
                            placeholder="08:00"
                            @change="setEndTime(item)"
                            :disabled="!canEditTime"
                        />

                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            style="margin-top: 16px; margin-left: -34px"
                        >
                            <g clip-path="url(#clip0_4009_55443)">
                                <path
                                    d="M9.99935 18.3346C14.6018 18.3346 18.3327 14.6038 18.3327 10.0013C18.3327 5.3988 14.6018 1.66797 9.99935 1.66797C5.39685 1.66797 1.66602 5.3988 1.66602 10.0013C1.66602 14.6038 5.39685 18.3346 9.99935 18.3346Z"
                                    stroke="#94A3B8"
                                    stroke-width="1.5"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M10.002 5V10.0042L13.5349 13.5375"
                                    stroke="#94A3B8"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </g>
                            <defs>
                                <clipPath id="clip0_4009_55443">
                                    <rect width="20" height="20" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    </div>
                </div>
                <div class="col-sm-6 col-xs-12">
                    <label class="form-label fw-semibold" :for="'end-time-' + index"
                        >{{ $t('planningModal.endTime') }} <span class="text-purple">*</span></label
                    >
                    <div class="d-flex">
                        <input
                            type="time"
                            :id="'end-time-' + index"
                            class="form-control"
                            style="background: transparent; border-radius: 14px !important; z-index: 50"
                            v-model="item.endTime"
                            placeholder="12:00"
                            :disabled="isEvent"
                        />
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            style="margin-top: 16px; margin-left: -34px"
                        >
                            <g clip-path="url(#clip0_4009_55443)">
                                <path
                                    d="M9.99935 18.3346C14.6018 18.3346 18.3327 14.6038 18.3327 10.0013C18.3327 5.3988 14.6018 1.66797 9.99935 1.66797C5.39685 1.66797 1.66602 5.3988 1.66602 10.0013C1.66602 14.6038 5.39685 18.3346 9.99935 18.3346Z"
                                    stroke="#94A3B8"
                                    stroke-width="1.5"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M10.002 5V10.0042L13.5349 13.5375"
                                    stroke="#94A3B8"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </g>
                            <defs>
                                <clipPath id="clip0_4009_55443">
                                    <rect width="20" height="20" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    </div>
                </div>

                <div class="col-sm-6 col-xs-12">
                    <label class="form-label fw-semibold" hidden for="price_ht"
                        >Prix HT (en {{ $symbolCurrency }})<span class="text-purple">*</span></label
                    >
                    <div class="input-group">
                        <input
                            hidden
                            type="number"
                            class="form-control"
                            v-model="item.priceHT"
                            :placeholder="$placeholderText('Prix HT (en ', $symbolCurrency, ')')"
                            @input="calculatePriceHT(index)"
                        />
                    </div>
                    <label class="form-label fw-semibold" for="vat_rate"
                        >{{ $t('planningModal.vatRate') }}<span class="text-purple">*</span></label
                    >
                    <div class="input-group">
                        <select v-model="item.vatRate" class="form-select form-control" @change="calculatePriceHT(index)">
                            <option v-for="vat in countryVatRates" :key="vat.id" :value="vat.pourcentage">
                                {{ vat.pourcentage }} - {{ vat.name }}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="col-sm-6 col-xs-12">
                    <label class="form-label fw-semibold" for="price"
                        >{{ $t('planningModal.sellingPrice') }}<span class="text-purple">*</span></label
                    >
                    <div class="input-group">
                        <input
                            type="number"
                            class="form-control"
                            v-model="item.price"
                            :placeholder="$placeholderText('Prix de vente (en ', $symbolCurrency, ')')"
                            @input="calculatePrice(index)"
                        />
                    </div>
                </div>

                <hr />
            </div>

            <div v-if="conflictMessage != ''" class="alert">
                <span>{{ conflictMessage }}</span>
            </div>
            <div v-if="errorMsg" class="alert">
                <span>{{ errorMsg }}</span>
            </div>
        </div>

        <div class="modal-footer flex-wrap gap-40">
            <button
                type="button"
                class="btn btn-outline-dark rounded-12 width-btn height_btn_page fw-semibold"
                data-bs-dismiss="modal"
                @click="closeModal"
            >
                {{ $t('common.cancel') }}
            </button>

            <button
                type="button"
                class="btn btn-dark height_btn_page rounded-12 width-btn fw-semibold"
                :class="{ 'button--is-active': isLoading }"
                :disabled="isLoading || (this.mode == 'create' && !canEditTime)"
                @click="handleSubmit"
            >
                <span class="button__text">{{ $t('common.validate') }}</span>
            </button>
        </div>
    </div>
</template>

<script>
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import { BModal } from 'bootstrap-vue'
import axios from 'axios'
import ConfirmationAlert from '../Adds/ConfirmationAlert.vue'
export default {
    props: {
        prestationId: {
            required: true,
        },
        countryVatRates: {
            required: true,
        },
        showModal: {
            required: true,
        },
        selectedSlot: {
            required: true,
        },
        prestation: {
            required: true,
        },
        mode: {
            required: true,
        },
        preDataInit: {
            required: true,
        },
        canEditTime: {
            required: true,
        },
    },
    data() {
        return {
            isLoading: false,
            selectedEvent: {},
            items: [],
            canChange: true,
            dateRange: {
                startDate: null,
                endDate: null,
            },
            ranges: {
                [this.$t('planningModal.ranges.today')]: [new Date(), new Date()],
                [this.$t('planningModal.ranges.next7days')]: [new Date(new Date().setDate(new Date().getDate() + 6)), new Date()],
                [this.$t('planningModal.ranges.next15days')]: [
                    new Date(new Date().setDate(new Date().getDate() + 14)),
                    new Date(),
                ],
                [this.$t('planningModal.ranges.next30days')]: [
                    new Date(new Date().setDate(new Date().getDate() + 29)),
                    new Date(),
                ],
                [this.$t('planningModal.ranges.thisMonth')]: [
                    new Date(new Date().getFullYear(), new Date().getMonth(), 1),
                    new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
                ],
                [this.$t('planningModal.ranges.nextMonth')]: [
                    new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1),
                    new Date(new Date().getFullYear(), new Date().getMonth() + 2, 0),
                ],
                [this.$t('planningModal.ranges.nextYear')]: [
                    new Date(new Date().getFullYear() + 1, 0, 1),
                    new Date(new Date().getFullYear() + 1, 11, 31),
                ],
            },
            conflictMessage: '',

            days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
            selectedDays: [],

            resolveFunction: null,
            rejectFunction: null,
            maxItemsNbr: 5,
            confirm: {
                code: null,
                text: null,
                title: null,
                showConfirmation: false,
            },
            isEvent: false,
            errorMsg: '',
        }
    },

    computed: {
        calculatedPriceHT() {
            return this.items.map((item) => {
                if (item.price && item.vatRate) {
                    return (parseFloat(item.price) / (1 + parseFloat(item.vatRate) / 100)).toFixed(2)
                }
                return ''
            })
        },
    },

    watch: {
        // showModal(newVal) {
        //     if (newVal) {
        //         console.log('newVal', newVal)
        //         this.addItem()
        //     }
        // },
    },

    methods: {
        translateDay(day) {
            return this.$t(`resource_planning.week_days.${day.toLowerCase()}`)
        },

        closeModal() {
            this.$modal.hide('planning-modal')
        },

        calculatePrice(index) {
            const item = this.items[index]
            if (item.vatRate !== '') {
                item.priceHT = parseFloat(item.price) / (1 + parseFloat(item.vatRate) / 100)
            }
        },

        calculatePriceHT(index) {
            const item = this.items[index]
            if (item.vatRate !== '') {
                item.price = parseFloat(item.priceHT) * (1 + parseFloat(item.vatRate) / 100)
            }
        },

        showEventDetails(event) {
            // console.log('event', event)
            this.selectedEvent = event
            this.showModal = true
        },

        pushItems(planningPrices) {
            for (let index = 0; index < planningPrices.length; index++) {
                const [hoursStart, minutesStart] = planningPrices[index].start_time.split(':').slice(0, 2)
                const [hoursEnd, minutesEnd] = planningPrices[index].end_time.split(':').slice(0, 2)

                this.items.push({
                    startTime: `${hoursStart}:${minutesStart}`,
                    endTime: `${hoursEnd}:${minutesEnd}`,
                    price: planningPrices[index].price,
                    price: parseFloat(planningPrices[index].price).toFixed(2),
                    priceHT: planningPrices[index].price_ht,
                    vatRate: planningPrices[index].vat_rate,
                    off_peak_time: planningPrices[index].off_peak_time,
                    authorize_mixing: planningPrices[index].authorize_mixing,
                })
            }
        },

        addItem() {
            this.items.push({
                startTime: '',
                endTime: '',
                price: '',
                priceHT: '',
                vatRate: '20',
                off_peak_time: true,
                authorize_mixing: true,
            })
        },

        deleteItem(index) {
            this.items.splice(index, 1)
        },

        isItemValid(item) {
            if (item.active === false) {
                return true
            }

            for (let key in item) {
                if (key !== 'active' && !item[key]) {
                    return false
                }
            }
            return true
        },
        formatDateToYMD(date) {
            const currentDate = new Date(date)
            const year = currentDate.getFullYear()
            const month = String(currentDate.getMonth() + 1).padStart(2, '0') // Month is zero-based
            const day = String(currentDate.getDate()).padStart(2, '0')
            const formattedDate = `${year}-${month}-${day}`
            return formattedDate
        },
        resetDate(data) {
            //  console.log('data data data.start', data.start, this.formatDateToYMD(data.start))

            this.dateRange.startDate = data.start ? this.formatDateToYMD(data.start) : null
            //console.log('datadata', data)
            if (data.end === null) {
                this.dateRange.endDate = data.start ? this.formatDateToYMD(data.start) : null
            } else {
                this.dateRange.endDate = data.end ? this.formatDateToYMD(data.end) : null
            }
            //console.log('this.dateRange', this.dateRange)
        },

        checkForConflicts() {
            for (let i = 0; i < this.items.length; i++) {
                const currentItem = this.items[i]
                const currentStart = new Date(`1970/01/01 ${currentItem.startTime}`)
                const currentEnd = new Date(`1970/01/01 ${currentItem.endTime}`)

                for (let j = i + 1; j < this.items.length; j++) {
                    const nextItem = this.items[j]
                    const nextStart = new Date(`1970/01/01 ${nextItem.startTime}`)
                    const nextEnd = new Date(`1970/01/01 ${nextItem.endTime}`)

                    if (
                        (currentStart < nextEnd && currentStart >= nextStart) ||
                        (nextStart < currentEnd && nextStart >= currentStart) ||
                        (currentStart <= nextStart && currentEnd >= nextEnd) ||
                        (nextStart <= currentStart && nextEnd >= currentEnd)
                    ) {
                        return true
                    }
                }
            }

            return false
        },
        open() {
            this.confirm.showConfirmation = true
            return new Promise((resolve, reject) => {
                this.resolveFunction = resolve
                this.rejectFunction = reject
            })
        },
        submitF() {
            this.conflictMessage = ''
            const hasConflict = this.checkForConflicts()
            if (hasConflict) {
                this.conflictMessage = this.$t('planningModal.conflictError')
            } else {
                let verifiedItems = this.items
                // this.items.filter((item) => this.isItemValid(item))

                const payload = {
                    days: this.selectedDays,
                    range: this.dateRange,
                    items: verifiedItems,
                    deleteBefore: this.isEvent,
                }

                this.submit(this.prestationId, 1, payload)
            }
        },
        async handleSubmit() {
            // console.log('rrrrrrrrrrrrrrrrr', this.isEvent, this.mode)
            if (this.isEvent || this.mode == 'edit') {
                try {
                    await this.open()
                    this.submitF()
                } catch (error) {
                    this.$toast.error(error.response.data.message)
                    //   console.log('Action canceled.')
                }
            } else {
                this.submitF()
            }
        },

        async submit(prestationId, establishmentId, payload) {
            this.isLoading = true
            this.errorMsg = null
            let _this = this
            axios
                .post(`/back-office/prestation/${prestationId}/planning/${establishmentId}`, payload)
                .then(function (response) {
                    _this.isLoading = false
                    _this.closeModal()
                    _this.$emit('refreshCalendar')
                    //   console.log(response)
                })
                .catch(function (error) {
                    _this.isLoading = false
                    console.log(' before this.errorMsg ', _this.errorMsg)

                    _this.errorMsg = error.response.data.message
                    console.log(' after this.errorMsg ', _this.errorMsg)
                })
        },
        cancelAction(code) {
            // console.log('cancelAction', code)
            this.rejectFunction()

            this.confirm.showConfirmation = false
        },
        confirmAction(code) {
            // console.log('confirmAction', code)
            this.resolveFunction()

            this.confirm.showConfirmation = false
        },
        displayDate(date) {
            const inputDate = new Date(date)
            const locale = this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US'
            const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
            const formattedDate = new Intl.DateTimeFormat(locale, options).format(inputDate)
            return formattedDate
        },
        setEndTime(item) {
            // console.log('setEndTime0', item, this.prestation)
            if (this.isEvent) item.endTime = this.addMinutesToTime(item.startTime, this.prestation.duration ?? 0)
        },
        addMinutesToTime(startTime, minutesToAdd) {
            // Parse the start time string into hours and minutes
            var [hours, minutes] = startTime.split(':').map(Number)

            // Convert the start time to minutes
            var totalMinutes = hours * 60 + minutes

            // Add the desired number of minutes
            totalMinutes += minutesToAdd

            // Calculate the new hours and minutes
            var newHours = Math.floor(totalMinutes / 60)
            var newMinutes = totalMinutes % 60

            // Format the result as a string
            var formattedResult = `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`

            return formattedResult
        },
    },

    mounted() {
        if (this.selectedSlot?.extendedProps?.planningPrices) {
            this.resetDate(this.selectedSlot)

            if (this.selectedSlot.extendedProps.planningPrices.length == 0) {
                this.addItem()
            } else {
                this.pushItems(this.selectedSlot.extendedProps.planningPrices)
            }
        } else {
            this.addItem()
            this.resetDate(this.selectedSlot)
        }
        this.isEvent = this.prestation?.subcategory?.slug?.toLowerCase() == 'events'

        if (this.prestation?.subcategory?.slug && this.isEvent) {
            this.canChange = false
            this.maxItemsNbr = 1
            if (this.mode == 'create') {
                this.confirm.text = ''
                this.confirm.title = this.$t('planningModal.reprogramTitle', {
                    date: this.displayDate(this.dateRange.startDate),
                })
                this.confirm.code = 14
            }
        }
        const timeRegex = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/

        if (this.isEvent)
            this.items.forEach((element, index) => {
                // console.log(
                //     element,
                //     'preDataInit',
                //     this.preDataInit,
                //     this.preDataInit.tva,
                //     this.prestation.vat_rate,
                //     this.preDataInit.priceHT ?? this.prestation.price_ht,
                //     isNaN(element.priceHT),
                //     isNaN(element.priceHT) || element.priceHT === ''
                // )

                // to make sure that tv not update we can check stattime is present or not
                if (!timeRegex.test(element.startTime)) element.vatRate = this.preDataInit.tva ?? this.prestation.vat_rate
                if (!timeRegex.test(element.startTime)) element.startTime = this.preDataInit.start
                if (!timeRegex.test(element.endTime)) element.endTime = this.preDataInit.end
                if (isNaN(element.priceHT) || element.priceHT === '')
                    element.priceHT = this.preDataInit.priceHT ?? this.prestation.price_ht
                this.calculatePriceHT(index)
            })
    },

    components: {
        BModal,
        DateRangePicker,
        ConfirmationAlert,
    },
}
</script>

<style lang="css">
.span-design {
    z-index: 1;
    margin-right: -57px;
    border-radius: 16px 0px 0px 16px;
}

.input-design {
    background: transparent;
    border-radius: 14px !important;
    z-index: 50;
    flex-direction: row-reverse;
    text-align: center;
    padding-right: 79px;
}

.btn-delete-date {
    display: flex;
    width: 32px;
    height: 32px;
    padding: 10px;
    padding-top: 11px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 32px;
    border: solid 1px #94a3b8 !important;
    background: #fff;
}

.btn-add-date {
    display: flex;
    width: 32px;
    height: 32px;
    padding: 10px;
    padding-top: 11px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 32px;
    background: #7d2dbd;
}

#close_btn {
    font-size: 1.7rem;
    cursor: pointer;
}

.custom-scroll {
    max-height: 100%;
    overflow-y: auto;
}

.modal-footer {
    display: -ms-flexbox;
    display: flex;
    -ms-flex-align: center;
    align-items: center;
    -ms-flex-pack: end;
    justify-content: center;
    padding: 1rem;
    border-bottom-right-radius: 0.3rem;
    border-bottom-left-radius: 0.3rem;
}

.left {
    transform: rotate(0deg) !important;
}

.right {
    transform: rotate(0deg);
}

@keyframes rotate {
    100% {
        transform: rotate(360deg);
    }
}

.add-btn {
    color: #ffffff;
}

.button {
    position: relative;
    display: inline-block;
    vertical-align: middle;
    text-align: center;
    margin: 0;
    cursor: pointer;
    appearance: none;
    transition: 0.3s all;
    text-decoration: none;

    background-color: #4285f4;
    color: #ffffff;
    border-radius: 3px;
    border: none;
    padding-top: 20px;
    padding-bottom: 20px;
    padding-left: 30px;
    padding-right: 30px;
    text-transform: uppercase;
    font-size: 15px;
    letter-spacing: 2px;

    &:hover,
    &:focus {
        background-color: #8cb4f5;
    }

    &:disabled {
        cursor: default;
    }
}

.button--is-active {
    .button__text {
        opacity: 0;
    }

    &:after {
        margin: -13px 0 0 -13px;
        width: 24px;
        height: 24px;
        position: absolute;
        left: 50%;
        top: 50%;
        content: '';
        border-radius: 24px;
        background-clip: padding-box;
        border: rgba(255, 255, 255, 0.25) 2px solid;
        border-top-color: white;
        animation: rotate 750ms linear infinite;
    }
}

.btn-danger {
    color: #fff;
}

.reportrange-text {
    padding: 15px !important;
    border-color: #e2e8f0 !important;
    border-radius: 8px !important;
}

.alert {
    margin-top: 1rem;
    padding: 20px;
    background-color: #f44336;
    color: white;
    opacity: 0.83;
    transition: opacity 0.6s;
    margin-bottom: 15px;
}
</style>
